import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import adminService from "./adminService";
import { setAlertError, setAlertSuccess } from "../alert/alertSlice";

export const deleteEntry = createAsyncThunk(
  "admin/deleteEntry",
  async (entryId, thunkAPI) => {
    try {
      const response = await adminService.deleteEntry(entryId);
      thunkAPI.dispatch(setAlertSuccess("Entry deleted successfully"));
      return response;
    } catch (error) {
      const errData = error.response?.data || null;
      const err = errData?.message || error.message || "Failed to delete entry";
      thunkAPI.dispatch(setAlertError(err));
      return thunkAPI.rejectWithValue(errData);
    }
  }
);

export const markEntryInvalid = createAsyncThunk(
  "admin/markEntryInvalid",
  async ({ entryId, reason }, thunkAPI) => {
    try {
      const response = await adminService.markEntryInvalid(entryId, reason);
      thunkAPI.dispatch(setAlertSuccess("Entry marked as invalid"));
      return response;
    } catch (error) {
      const errData = error.response?.data || null;
      const err =
        errData?.message || error.message || "Failed to mark entry as invalid";
      thunkAPI.dispatch(setAlertError(err));
      return thunkAPI.rejectWithValue(errData);
    }
  }
);

export const restoreEntry = createAsyncThunk(
  "admin/restoreEntry",
  async (entryId, thunkAPI) => {
    try {
      const response = await adminService.restoreEntry(entryId);
      thunkAPI.dispatch(setAlertSuccess("Entry restored successfully"));
      return response;
    } catch (error) {
      const errData = error.response?.data || null;
      const err =
        errData?.message || error.message || "Failed to restore entry";
      thunkAPI.dispatch(setAlertError(err));
      return thunkAPI.rejectWithValue(errData);
    }
  }
);

export const recalculateAllEntries = createAsyncThunk(
  "admin/recalculateAllEntries",
  async (data, thunkAPI) => {
    try {
      const response = await adminService.recalculateAllEntries(data);
      thunkAPI.dispatch(
        setAlertSuccess("Recalculation of all entries initiated")
      );
      return response;
    } catch (error) {
      const errData = error.response?.data || null;
      const err =
        errData?.message || error.message || "Failed to recalculate entries";
      thunkAPI.dispatch(setAlertError(err));
      return thunkAPI.rejectWithValue(errData);
    }
  }
);

const initialState = {
  isLoading: false,
  isSuccess: false,
  isError: false,
  message: "",
};

const adminSlice = createSlice({
  name: "admin",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(deleteEntry.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteEntry.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.message = "Entry deleted successfully";
      })
      .addCase(deleteEntry.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(markEntryInvalid.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(markEntryInvalid.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.message = "Entry marked as invalid";
      })
      .addCase(markEntryInvalid.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(restoreEntry.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(restoreEntry.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.message = "Entry restored successfully";
      })
      .addCase(restoreEntry.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(recalculateAllEntries.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(recalculateAllEntries.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.message = "Recalculation of all entries initiated";
      })
      .addCase(recalculateAllEntries.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      });
  },
});

export default adminSlice.reducer;
